import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';

const SPORT_OPTIONS = [
  ['rowing', 'Rowing'],
  ['sailing', 'Sailing'],
];

const STATUS_OPTIONS = [
  ['open', 'Open'],
  ['in_progress', 'In progress'],
  ['waiting', 'Waiting'],
  ['resolved', 'Resolved'],
  ['cancelled', 'Cancelled'],
];

const BLOCKING_STATUSES = new Set(['open', 'in_progress', 'waiting']);
const EMPTY_BOATS = [];

function SettingsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M19.14 12.94a7.77 7.77 0 0 0 .05-.94 7.77 7.77 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.06 7.06 0 0 0-1.63-.94l-.36-2.54a.5.5 0 0 0-.49-.42h-3.84a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.7 8.84a.5.5 0 0 0 .12.64l2.03 1.58a7.77 7.77 0 0 0-.05.94c0 .32.02.63.05.94L2.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54a.5.5 0 0 0 .49.42h3.84a.5.5 0 0 0 .49-.42l.36-2.54a7.06 7.06 0 0 0 1.63-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z" />
    </svg>
  );
}

function sportLabel(sport) {
  return SPORT_OPTIONS.find(([value]) => value === sport)?.[1] || 'Boats';
}

function statusLabel(status) {
  return STATUS_OPTIONS.find(([value]) => value === status)?.[1] || status || 'Open';
}

function timestampMillis(value) {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  return 0;
}

function dateLabel(value) {
  const date = typeof value?.toDate === 'function' ? value.toDate() : null;
  if (!date) return '';
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function compareByName(left, right) {
  return String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' });
}

function emptyDraft(defaultSport) {
  return {
    sport: defaultSport || 'rowing',
    boatId: '',
    title: '',
    description: '',
    takesBoatOutOfUse: true,
  };
}

function cleanText(value) {
  return String(value || '').trim();
}

function safeBoatOption(docSnap, sport, { includeAdminOnly = false } = {}) {
  const boat = docSnap.data() || {};
  if (!includeAdminOnly && sport === 'sailing' && boat.visibility === 'admins') return null;
  return {
    id: docSnap.id,
    sport,
    name: cleanText(boat.name || boat.displayName || '') || 'Unnamed boat',
    type: cleanText(boat.type || ''),
    className: cleanText(boat.className || ''),
    status: cleanText(boat.status || 'active') || 'active',
  };
}

function boatOptionLabel(boat) {
  return [
    boat?.name || 'Unnamed boat',
    boat?.type || boat?.className || '',
    String(boat?.status || 'active') === 'maintenance' ? 'out of order' : '',
  ].filter(Boolean).join(' · ');
}

function BoatPicker({
  boats,
  value,
  onChange,
  loading = false,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const selectedBoat = useMemo(
    () => boats.find((boat) => boat.id === value) || null,
    [boats, value]
  );
  const filteredBoats = useMemo(() => {
    const term = queryText.trim().toLowerCase();
    if (!term) return boats;
    return boats.filter((boat) => (
      [
        boat.name,
        boat.type,
        boat.className,
        String(boat.status || 'active') === 'maintenance' ? 'out of order' : '',
      ].filter(Boolean).join(' ').toLowerCase().includes(term)
    ));
  }, [boats, queryText]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickAway = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQueryText('');
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open]);

  const chooseBoat = (boatId) => {
    onChange(boatId);
    setOpen(false);
    setQueryText('');
  };

  return (
    <div className="boat-maintenance-boat-picker" ref={rootRef}>
      <span className="form-label">Boat (required)</span>
      <button
        type="button"
        className={`boat-maintenance-boat-picker__control ${open ? 'is-open' : ''}`}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={selectedBoat ? 'boat-maintenance-boat-picker__value' : 'boat-maintenance-boat-picker__placeholder'}>
          {selectedBoat ? boatOptionLabel(selectedBoat) : 'Choose a boat'}
        </span>
        <span className="boat-maintenance-boat-picker__chevron" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="boat-maintenance-boat-picker__menu">
          <input
            type="search"
            className="input boat-maintenance-boat-picker__search"
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder="Search boats"
            autoFocus
          />
          <div className="boat-maintenance-boat-picker__options" role="listbox" aria-label="Boats">
            {loading && <div className="boat-maintenance-boat-picker__empty">Loading boats...</div>}
            {!loading && filteredBoats.length === 0 && (
              <div className="boat-maintenance-boat-picker__empty">
                {boats.length === 0 ? 'No boats available.' : 'No boats match that search.'}
              </div>
            )}
            {!loading && filteredBoats.map((boat) => {
              const selected = boat.id === value;
              return (
                <button
                  key={boat.id}
                  type="button"
                  className={`boat-maintenance-boat-picker__option ${selected ? 'is-selected' : ''}`}
                  onClick={() => chooseBoat(boat.id)}
                  role="option"
                  aria-selected={selected}
                >
                  <strong>{boat.name || 'Unnamed boat'}</strong>
                  <span>{[boat.type || boat.className || '', String(boat.status || 'active') === 'maintenance' ? 'out of order' : ''].filter(Boolean).join(' · ') || 'Active'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BoatMaintenanceCard({
  teamId,
  modules = {},
  allowedSports = ['rowing', 'sailing'],
  canManageRowing = false,
  canManageSailing = false,
  canReportRowing = false,
  canReportSailing = false,
  canViewRowingTickets = false,
  canViewSailingTickets = false,
  createTicketRequest = null,
  onCreateTicketRequestHandled,
}) {
  const [tickets, setTickets] = useState([]);
  const [updatesByTicket, setUpdatesByTicket] = useState({});
  const [boatsBySport, setBoatsBySport] = useState({ rowing: [], sailing: [] });
  const [boatsLoading, setBoatsLoading] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [statusFilter, setStatusFilter] = useState('blocking');
  const [sportFilter, setSportFilter] = useState('all');
  const [draft, setDraft] = useState(() => emptyDraft(canManageRowing || canReportRowing ? 'rowing' : 'sailing'));
  const [updateNote, setUpdateNote] = useState('');
  const [settingsSaving, setSettingsSaving] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [ticketDetailsOpen, setTicketDetailsOpen] = useState(false);
  const [boatLoadError, setBoatLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const handledCreateTicketRequestId = useRef(null);

  const allowedSportSet = useMemo(() => new Set(
    (Array.isArray(allowedSports) ? allowedSports : [])
      .map((sport) => String(sport || '').trim())
      .filter((sport) => sport === 'rowing' || sport === 'sailing')
  ), [allowedSports]);

  const adminSports = useMemo(() => [
    canManageRowing ? 'rowing' : null,
    canManageSailing ? 'sailing' : null,
  ].filter((sport) => sport && allowedSportSet.has(sport)), [allowedSportSet, canManageRowing, canManageSailing]);

  const reportSports = useMemo(() => [
    modules?.rowing?.boatMaintenance?.enabled === true && canReportRowing ? 'rowing' : null,
    modules?.sailing?.boatMaintenance?.enabled === true && canReportSailing ? 'sailing' : null,
  ].filter((sport) => sport && allowedSportSet.has(sport)), [allowedSportSet, canReportRowing, canReportSailing, modules?.rowing?.boatMaintenance?.enabled, modules?.sailing?.boatMaintenance?.enabled]);

  const ticketViewSports = useMemo(() => [
    modules?.rowing?.boatMaintenance?.enabled === true && canViewRowingTickets ? 'rowing' : null,
    modules?.sailing?.boatMaintenance?.enabled === true && canViewSailingTickets ? 'sailing' : null,
  ].filter((sport) => sport && allowedSportSet.has(sport)), [allowedSportSet, canViewRowingTickets, canViewSailingTickets, modules?.rowing?.boatMaintenance?.enabled, modules?.sailing?.boatMaintenance?.enabled]);

  const formSports = useMemo(() => Array.from(new Set([...adminSports, ...reportSports])), [adminSports, reportSports]);
  const ticketSports = useMemo(() => Array.from(new Set([...adminSports, ...ticketViewSports])), [adminSports, ticketViewSports]);
  const visibleSports = useMemo(() => Array.from(new Set([...formSports, ...ticketSports])), [formSports, ticketSports]);
  const reportOnlySports = useMemo(
    () => reportSports.filter((sport) => !adminSports.includes(sport)),
    [adminSports, reportSports]
  );
  const isAdmin = adminSports.length > 0;
  const canCreateTickets = formSports.length > 0;
  const canViewTickets = ticketSports.length > 0;

  useEffect(() => {
    if (!formSports.includes(draft.sport)) {
      setDraft(emptyDraft(formSports[0] || 'rowing'));
    }
  }, [draft.sport, formSports]);

  useEffect(() => {
    const sport = cleanText(createTicketRequest?.sport);
    const boatId = cleanText(createTicketRequest?.boatId);
    const requestId = createTicketRequest?.requestId;
    if (!boatId || !formSports.includes(sport) || handledCreateTicketRequestId.current === requestId) return;
    handledCreateTicketRequestId.current = requestId;
    setDraft({
      ...emptyDraft(sport),
      boatId,
    });
    setError('');
    setSuccess('');
    setCreateFormOpen(true);
  }, [createTicketRequest, formSports]);

  const closeCreateForm = () => {
    setCreateFormOpen(false);
    if (createTicketRequest) onCreateTicketRequestHandled?.();
  };

  useEffect(() => {
    if (ticketSports.length <= 1 || (sportFilter !== 'all' && !ticketSports.includes(sportFilter))) {
      setSportFilter('all');
    }
  }, [sportFilter, ticketSports]);

  useEffect(() => {
    if (!teamId || ticketSports.length === 0) {
      setTickets([]);
      return undefined;
    }
    const unsubs = ticketSports.map((sport) => safeOnSnapshot(
      query(collection(db, 'teams', teamId, 'boatMaintenanceTickets'), where('sport', '==', sport)),
      (snapshot) => {
        setTickets((current) => {
          const rest = current.filter((ticket) => ticket.sport !== sport);
          const next = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
          return [...rest, ...next].sort((left, right) => timestampMillis(right.updatedAt) - timestampMillis(left.updatedAt));
        });
      },
      undefined,
      { label: 'boat-maintenance-tickets', teamId, sport }
    ));
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [teamId, ticketSports]);

  useEffect(() => {
    if (!teamId || adminSports.length === 0) {
      setBoatsBySport((current) => ({
        rowing: reportOnlySports.includes('rowing') ? current.rowing : [],
        sailing: reportOnlySports.includes('sailing') ? current.sailing : [],
      }));
      return undefined;
    }
    const unsubs = adminSports.map((sport) => {
      const collectionName = sport === 'sailing' ? 'sailingBoats' : 'boats';
      return safeOnSnapshot(collection(db, 'teams', teamId, collectionName), (snapshot) => {
        setBoatsBySport((current) => ({
          ...current,
          [sport]: snapshot.docs
            .map((docSnap) => safeBoatOption(docSnap, sport, { includeAdminOnly: true }))
            .filter(Boolean)
            .sort(compareByName),
        }));
      }, undefined, { label: 'boat-maintenance-fleet', teamId, sport });
    });
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [adminSports, reportOnlySports, teamId]);

  useEffect(() => {
    if (!teamId || formSports.length === 0) return undefined;
    let cancelled = false;
    const loadReportableBoats = async () => {
      setBoatsLoading(true);
      setBoatLoadError('');
      try {
        const results = await Promise.all(formSports.map(async (sport) => {
          const callable = httpsCallable(functions, 'listBoatMaintenanceBoats');
          const result = await callable({ teamId, sport });
          const boats = (Array.isArray(result?.data?.boats) ? result.data.boats : [])
            .map((boat) => ({ ...boat, sport }))
            .sort(compareByName);
          return [sport, boats];
        }));
        if (cancelled) return;
        setBoatsBySport((current) => {
          const next = { ...current };
          results.forEach(([sport, boats]) => {
            next[sport] = boats;
          });
          ['rowing', 'sailing'].forEach((sport) => {
            if (!adminSports.includes(sport) && !reportOnlySports.includes(sport)) next[sport] = [];
          });
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          setBoatLoadError(err?.message || 'Failed to load boats for reporting.');
        }
      } finally {
        if (!cancelled) setBoatsLoading(false);
      }
    };
    loadReportableBoats();
    return () => {
      cancelled = true;
    };
  }, [adminSports, formSports, reportOnlySports, teamId]);

  useEffect(() => {
    const selected = tickets.find((ticket) => ticket.id === selectedTicketId) || null;
    const canReadSelectedUpdates = selected && ticketSports.includes(selected.sport);
    if (!teamId || !selectedTicketId || !canReadSelectedUpdates) {
      setUpdatesByTicket({});
      return undefined;
    }
    const unsubscribe = safeOnSnapshot(
      collection(db, 'teams', teamId, 'boatMaintenanceTickets', selectedTicketId, 'updates'),
      (snapshot) => {
        const updates = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((left, right) => timestampMillis(right.createdAt) - timestampMillis(left.createdAt));
        setUpdatesByTicket((current) => ({ ...current, [selectedTicketId]: updates }));
      },
      undefined,
      { label: 'boat-maintenance-updates', teamId, ticketId: selectedTicketId }
    );
    return () => unsubscribe();
  }, [selectedTicketId, teamId, tickets, ticketSports]);

  const selectedTicket = useMemo(
    () => tickets.find((ticket) => ticket.id === selectedTicketId) || null,
    [selectedTicketId, tickets]
  );

  const blockingTicketBoatKeys = useMemo(() => new Set(
    tickets
      .filter((ticket) => BLOCKING_STATUSES.has(String(ticket.status || 'open')) && ticket.takesBoatOutOfUse !== false)
      .map((ticket) => `${ticket.sport}:${ticket.boatId}`)
  ), [tickets]);

  const untrackedMaintenanceBoats = useMemo(() => (
    adminSports.flatMap((sport) => (
      (boatsBySport[sport] || [])
        .filter((boat) => String(boat.status || 'active') === 'maintenance')
        .filter((boat) => !blockingTicketBoatKeys.has(`${sport}:${boat.id}`))
        .map((boat) => ({ ...boat, sport }))
    ))
  ), [adminSports, blockingTicketBoatKeys, boatsBySport]);

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      if (sportFilter !== 'all' && ticket.sport !== sportFilter) return false;
      const status = String(ticket.status || 'open');
      if (statusFilter === 'blocking') return BLOCKING_STATUSES.has(status);
      if (statusFilter === 'closed') return !BLOCKING_STATUSES.has(status);
      return status === statusFilter;
    });
  }, [sportFilter, statusFilter, tickets]);

  const availableBoats = useMemo(() => boatsBySport[draft.sport] || EMPTY_BOATS, [boatsBySport, draft.sport]);
  const availableBoatIds = useMemo(() => new Set(availableBoats.map((boat) => boat.id)), [availableBoats]);
  const updates = selectedTicket ? updatesByTicket[selectedTicket.id] || [] : [];
  const selectedBoatOpenTickets = useMemo(() => {
    if (!draft.boatId || !ticketSports.includes(draft.sport)) return [];
    return tickets
      .filter((ticket) => ticket.sport === draft.sport)
      .filter((ticket) => ticket.boatId === draft.boatId)
      .filter((ticket) => BLOCKING_STATUSES.has(String(ticket.status || 'open')))
      .sort((left, right) => timestampMillis(right.updatedAt) - timestampMillis(left.updatedAt));
  }, [draft.boatId, draft.sport, ticketSports, tickets]);

  useEffect(() => {
    if (!draft.boatId || boatsLoading) return;
    if (availableBoats.length === 0 || availableBoatIds.has(draft.boatId)) return;
    setDraft((current) => ({ ...current, boatId: '' }));
  }, [availableBoatIds, availableBoats.length, boatsLoading, draft.boatId]);

  const callFunction = async (name, payload) => {
    const callable = httpsCallable(functions, name);
    return callable(payload);
  };

  const submitTicket = async (event) => {
    event.preventDefault();
    const title = cleanText(draft.title);
    const boatId = cleanText(draft.boatId);
    if (!title || !boatId) {
      setError('Choose a boat and add a title.');
      return;
    }
    if (!availableBoatIds.has(boatId)) {
      setError('Choose a boat from the list before submitting.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const result = await callFunction('createBoatMaintenanceTicket', {
        teamId,
        sport: draft.sport,
        boatId,
        title,
        description: cleanText(draft.description),
        takesBoatOutOfUse: draft.takesBoatOutOfUse !== false,
      });
      setDraft(emptyDraft(draft.sport));
      closeCreateForm();
      if (result?.data?.ticketId) {
        setSelectedTicketId(result.data.ticketId);
      }
      setSuccess('Maintenance ticket created.');
    } catch (err) {
      setError(err?.message || 'Failed to create maintenance ticket.');
    } finally {
      setBusy(false);
    }
  };

  const addUpdate = async (event) => {
    event.preventDefault();
    if (!selectedTicket || !cleanText(updateNote)) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await callFunction('addBoatMaintenanceUpdate', {
        teamId,
        ticketId: selectedTicket.id,
        note: cleanText(updateNote),
      });
      setUpdateNote('');
    } catch (err) {
      setError(err?.message || 'Failed to add update.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (ticket, status) => {
    if (!ticket?.id) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await callFunction('setBoatMaintenanceTicketStatus', {
        teamId,
        ticketId: ticket.id,
        status,
      });
      setSuccess(`Ticket marked ${statusLabel(status).toLowerCase()}.`);
    } catch (err) {
      setError(err?.message || 'Failed to update ticket status.');
    } finally {
      setBusy(false);
    }
  };

  const setTicketImpact = async (ticket, takesBoatOutOfUse) => {
    if (!ticket?.id) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await callFunction('setBoatMaintenanceTicketImpact', {
        teamId,
        ticketId: ticket.id,
        takesBoatOutOfUse,
      });
      setSuccess(takesBoatOutOfUse ? 'Boat marked out of use.' : 'Boat can remain in use for this ticket.');
    } catch (err) {
      setError(err?.message || 'Failed to update the boat availability impact.');
    } finally {
      setBusy(false);
    }
  };

  const saveMaintenanceSetting = async (sport, patch) => {
    setSettingsSaving(sport);
    setError('');
    setSuccess('');
    try {
      await callFunction('saveBoatMaintenanceSettings', {
        teamId,
        sport,
        ...patch,
      });
      setSuccess(`${sportLabel(sport)} maintenance setting saved.`);
    } catch (err) {
      setError(err?.message || 'Failed to save reporting setting.');
    } finally {
      setSettingsSaving('');
    }
  };

  if (visibleSports.length === 0) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Boat Maintenance</span>
        {isAdmin && (
          <button
            type="button"
            className="edit-btn"
            onClick={() => setSettingsOpen(true)}
            aria-label="Boat maintenance settings"
            title="Boat maintenance settings"
          >
            <SettingsIcon />
          </button>
        )}
      </div>
      <div className="card-body">
        {error && <p className="form-error">{error}</p>}
        {success && <p className="text-muted">{success}</p>}

        <div className="boat-maintenance-card__summary">
          {canViewTickets && (
            <div className="boat-maintenance-card__stats" aria-label="Boat maintenance summary">
              <span>
                <strong>{tickets.filter((ticket) => BLOCKING_STATUSES.has(String(ticket.status || 'open'))).length}</strong>
                Open
              </span>
              <span>
                <strong>{tickets.filter((ticket) => BLOCKING_STATUSES.has(String(ticket.status || 'open')) && ticket.takesBoatOutOfUse !== false).length}</strong>
                Out of use
              </span>
              {isAdmin && (
                <span>
                  <strong>{untrackedMaintenanceBoats.length}</strong>
                  Untracked
                </span>
              )}
            </div>
          )}
          {canCreateTickets && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setCreateFormOpen(true)}
            >
              {isAdmin ? 'Create ticket' : 'Report issue'}
            </button>
          )}
        </div>

        {canViewTickets && (
          <div className="boat-maintenance-card__panel">
            <div className="boat-maintenance-card__filters">
              {ticketSports.length > 1 && (
                <select className="input" value={sportFilter} onChange={(event) => setSportFilter(event.target.value)}>
                  <option value="all">All sports</option>
                  {ticketSports.map((sport) => <option key={sport} value={sport}>{sportLabel(sport)}</option>)}
                </select>
              )}
              <select className="input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="blocking">Open tickets</option>
                <option value="closed">Closed</option>
                {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>

            {filteredTickets.length === 0 ? (
              <p className="text-muted boat-maintenance-card__empty">No maintenance tickets match this filter.</p>
            ) : (
              <div className="boat-maintenance-card__ticket-list">
                {filteredTickets.slice(0, 12).map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    className="boat-maintenance-card__ticket"
                    onClick={() => {
                      setSelectedTicketId(ticket.id);
                      setTicketDetailsOpen(true);
                    }}
                  >
                    <span className="boat-maintenance-card__ticket-main">
                      <strong>{ticket.title || 'Maintenance ticket'}</strong>
                      <small>
                        {sportLabel(ticket.sport)} · {ticket.boatNameSnapshot || 'Boat'} · {ticket.takesBoatOutOfUse !== false ? 'Out of use' : 'Boat in use'} · {dateLabel(ticket.updatedAt) || 'No updates yet'}
                      </small>
                    </span>
                    <span className={`boat-maintenance-card__status boat-maintenance-card__status--${String(ticket.status || 'open')}`}>
                      {statusLabel(ticket.status || 'open')}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {isAdmin && untrackedMaintenanceBoats.length > 0 && (
              <div className="boat-maintenance-card__untracked">
                <strong>Out of order without a ticket</strong>
                <div>
                  {untrackedMaintenanceBoats.slice(0, 4).map((boat) => (
                    <button
                      key={`${boat.sport}-${boat.id}`}
                      type="button"
                      className="btn btn--secondary btn--xs"
                      onClick={() => {
                        setDraft({
                          sport: boat.sport,
                          boatId: boat.id,
                          title: `${boat.name || 'Boat'} maintenance`,
                          description: '',
                          takesBoatOutOfUse: true,
                        });
                        setCreateFormOpen(true);
                      }}
                    >
                      {boat.name || 'Boat'}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!canViewTickets && canCreateTickets && (
          <p className="text-muted boat-maintenance-card__empty">
            Report a boat issue and the club team will pick it up.
          </p>
        )}

        {createFormOpen && canCreateTickets && (
          <div className="modal-backdrop boat-maintenance-modal-backdrop" onClick={closeCreateForm}>
            <div
              className="modal-content boat-maintenance-modal boat-maintenance-modal--create"
              role="dialog"
              aria-modal="true"
              aria-labelledby="boat-maintenance-create-title"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 id="boat-maintenance-create-title" className="modal-title boat-maintenance-create-form__title">{isAdmin ? 'Create Ticket' : 'Report Boat Issue'}</h2>
              <form onSubmit={submitTicket} className="modal-form boat-maintenance-create-form">
                <div className="boat-maintenance-create-form__grid">
                  {formSports.length > 1 && (
                    <label className="form-label">Sport
                      <select className="input" value={draft.sport} onChange={(event) => setDraft((current) => ({ ...emptyDraft(event.target.value), title: current.title, description: current.description }))}>
                        {formSports.map((sport) => <option key={sport} value={sport}>{sportLabel(sport)}</option>)}
                      </select>
                    </label>
                  )}
                  <BoatPicker
                    key={draft.sport}
                    boats={availableBoats}
                    value={draft.boatId}
                    onChange={(boatId) => setDraft((current) => ({ ...current, boatId }))}
                    loading={boatsLoading}
                  />
                </div>
                {boatLoadError && <p className="form-error">Could not load the boat list: {boatLoadError}</p>}
                {selectedBoatOpenTickets.length > 0 && (
                  <div className="boat-maintenance-existing">
                    <div className="boat-maintenance-existing__header">
                      <strong>Open tickets for this boat</strong>
                      <span>{selectedBoatOpenTickets.length}</span>
                    </div>
                    <p className="boat-maintenance-existing__hint">You can still create a separate ticket for a different issue.</p>
                    <div className="boat-maintenance-existing__list">
                      {selectedBoatOpenTickets.map((ticket) => (
                        <button
                          key={ticket.id}
                          type="button"
                          className="boat-maintenance-existing__ticket"
                          onClick={() => {
                            setSelectedTicketId(ticket.id);
                            closeCreateForm();
                            setTicketDetailsOpen(true);
                          }}
                        >
                          <span>
                            <strong>{ticket.title || 'Maintenance ticket'}</strong>
                            <small>{[statusLabel(ticket.status || 'open'), dateLabel(ticket.updatedAt)].filter(Boolean).join(' · ')}</small>
                          </span>
                          <span>View</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <label className="form-label">Reason
                  <input className="input" value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} placeholder="e.g. Rudder damage" />
                </label>
                <label className="form-label">Details
                  <textarea className="input" value={draft.description} onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))} />
                </label>
                <label className="form-toggle-row boat-maintenance-create-form__impact">
                  <span>
                    <strong>Take boat out of use</strong>
                    <small>Turn this off for issues that do not prevent safe use.</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={draft.takesBoatOutOfUse !== false}
                    onChange={(event) => setDraft((current) => ({ ...current, takesBoatOutOfUse: event.target.checked }))}
                  />
                </label>
                <div className="form-actions boat-maintenance-create-form__actions">
                  <button type="button" className="btn btn--secondary" onClick={closeCreateForm}>Cancel</button>
                  <button type="submit" className="btn btn--primary" disabled={busy || !draft.boatId || !availableBoatIds.has(draft.boatId)}>{busy ? 'Saving...' : isAdmin ? 'Create ticket' : 'Report issue'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {ticketDetailsOpen && canViewTickets && selectedTicket && (
          <div className="modal-backdrop" onClick={() => setTicketDetailsOpen(false)}>
            <div
              className="modal-content boat-maintenance-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="boat-maintenance-ticket-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="boat-maintenance-modal__header">
                <div className="boat-maintenance-modal__title-group">
                  <h2 id="boat-maintenance-ticket-title" className="modal-title">{selectedTicket.title || 'Maintenance Ticket'}</h2>
                  <p className="boat-maintenance-modal__meta">
                    {sportLabel(selectedTicket.sport)} · {selectedTicket.boatNameSnapshot || 'Boat'} · {selectedTicket.takesBoatOutOfUse !== false ? 'Out of use' : 'Boat remains in use'}
                  </p>
                </div>
                <span className={`boat-maintenance-card__status boat-maintenance-card__status--${String(selectedTicket.status || 'open')}`}>
                  {statusLabel(selectedTicket.status || 'open')}
                </span>
              </div>
              {selectedTicket.description && (
                <div className="boat-maintenance-modal__description">
                  <p>{selectedTicket.description}</p>
                </div>
              )}
              {isAdmin && (
                <>
                  <div className="boat-maintenance-card__actions boat-maintenance-modal__actions" aria-label="Maintenance ticket actions">
                    {BLOCKING_STATUSES.has(String(selectedTicket.status || 'open')) && (
                      <button
                        type="button"
                        className="btn btn--secondary btn--xs"
                        onClick={() => setTicketImpact(selectedTicket, selectedTicket.takesBoatOutOfUse === false)}
                        disabled={busy}
                      >
                        {selectedTicket.takesBoatOutOfUse !== false ? 'Allow boat use' : 'Take boat out of use'}
                      </button>
                    )}
                    {BLOCKING_STATUSES.has(String(selectedTicket.status || 'open')) ? (
                      <>
                        <button type="button" className="btn btn--secondary btn--xs" onClick={() => setStatus(selectedTicket, 'in_progress')} disabled={busy}>In progress</button>
                        <button type="button" className="btn btn--secondary btn--xs" onClick={() => setStatus(selectedTicket, 'waiting')} disabled={busy}>Waiting</button>
                        <button type="button" className="btn btn--primary btn--xs" onClick={() => setStatus(selectedTicket, 'resolved')} disabled={busy}>Resolve</button>
                        <button type="button" className="btn btn--secondary btn--xs" onClick={() => setStatus(selectedTicket, 'cancelled')} disabled={busy}>Cancel ticket</button>
                      </>
                    ) : (
                      <button type="button" className="btn btn--secondary btn--xs" onClick={() => setStatus(selectedTicket, 'open')} disabled={busy}>Reopen</button>
                    )}
                  </div>
                  <form onSubmit={addUpdate} className="boat-maintenance-composer">
                    <label className="boat-maintenance-composer__label" htmlFor="boat-maintenance-update-note">Add update</label>
                    <textarea
                      id="boat-maintenance-update-note"
                      className="input boat-maintenance-composer__input"
                      value={updateNote}
                      onChange={(event) => setUpdateNote(event.target.value)}
                      placeholder="Add the latest repair notes, parts needed, or next step."
                    />
                    <div className="boat-maintenance-composer__footer">
                      <span className="text-muted">{cleanText(updateNote).length}/1200</span>
                      <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !cleanText(updateNote)}>Post update</button>
                    </div>
                  </form>
                </>
              )}
              <div className="boat-maintenance-card__updates">
                <h3>Updates</h3>
                {updates.length === 0 && <p className="text-muted">No updates yet.</p>}
                {updates.map((update) => (
                  <div key={update.id} className="boat-maintenance-card__update">
                    <span className="boat-maintenance-card__update-marker" aria-hidden="true" />
                    <div>
                      <strong>{update.type === 'status' ? `Status: ${statusLabel(update.status)}` : update.type === 'created' ? 'Ticket created' : update.type === 'impact' ? (update.takesBoatOutOfUse ? 'Boat taken out of use' : 'Boat returned to use') : 'Update'}</strong>
                      <small>
                        {[update.createdByName || 'Admin', dateLabel(update.createdAt)].filter(Boolean).join(' · ')}
                      </small>
                      {update.note && <p>{update.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn--secondary" onClick={() => setTicketDetailsOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {settingsOpen && (
          <div className="modal-backdrop" onClick={() => setSettingsOpen(false)}>
            <div
              className="modal-content"
              role="dialog"
              aria-modal="true"
              aria-labelledby="boat-maintenance-settings-title"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 id="boat-maintenance-settings-title" className="modal-title">Boat Maintenance Settings</h2>
              <p className="text-muted" style={{ marginTop: 0 }}>
                Choose whether members can report issues and see existing ticket progress.
              </p>
              <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                {adminSports.map((sport) => {
                  const reportingEnabled = modules?.[sport]?.boatMaintenance?.allowMemberReports === true;
                  const viewingEnabled = modules?.[sport]?.boatMaintenance?.allowMemberTicketViewing === true;
                  return (
                    <React.Fragment key={sport}>
                      <label className="form-toggle-row" style={{ margin: 0 }}>
                        <span>{sportLabel(sport)} members can report issues</span>
                        <input
                          type="checkbox"
                          checked={reportingEnabled}
                          disabled={settingsSaving === sport}
                          onChange={(event) => saveMaintenanceSetting(sport, { allowMemberReports: event.target.checked })}
                        />
                      </label>
                      <label className="form-toggle-row" style={{ margin: 0 }}>
                        <span>{sportLabel(sport)} members can see existing tickets</span>
                        <input
                          type="checkbox"
                          checked={viewingEnabled}
                          disabled={settingsSaving === sport}
                          onChange={(event) => saveMaintenanceSetting(sport, { allowMemberTicketViewing: event.target.checked })}
                        />
                      </label>
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn--secondary" onClick={() => setSettingsOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
